import { FormatMessageFunction } from '@common/types/Translation';
import { EMAIL_REGEX } from '@constants/common';
import * as Yup from 'yup';

export const createSignUpValidationSchema = (formatMessage: FormatMessageFunction) =>
    Yup.object({
        first_name: Yup.string()
            .required(
                formatMessage(
                    {
                        id: 'schema.errors.required',
                        defaultMessage: '{field} is required',
                        description: 'Error message displayed when user leaves a mandatory field empty. Used in forms',
                    },
                    { field: formatMessage({ id: 'common.firstName', defaultMessage: 'First name' }) }
                )
            )
            .min(2, ({ min }) =>
                formatMessage(
                    {
                        id: 'schema.errors.min',
                        defaultMessage: '{field} must be at least {min} characters long',
                        description:
                            'Error message displayed when user enters a value that is shorter than the required minimum length. Used in forms',
                    },
                    { field: formatMessage({ id: 'common.firstName', defaultMessage: 'First name' }), min }
                )
            )
            .max(50, ({ max }) =>
                formatMessage(
                    {
                        id: 'schema.errors.max',
                        defaultMessage: '{field} cannot exceed {max} characters',
                        description:
                            'Error message displayed when user enters a value that exceeds the maximum allowed length. Used in forms',
                    },
                    { field: formatMessage({ id: 'common.firstName', defaultMessage: 'First name' }), max }
                )
            ),
        last_name: Yup.string()
            .required(
                formatMessage(
                    {
                        id: 'schema.errors.required',
                        defaultMessage: '{field} is required',
                        description: 'Error message displayed when user leaves a mandatory field empty. Used in forms',
                    },
                    { field: formatMessage({ id: 'common.lastName', defaultMessage: 'Last name' }) }
                )
            )
            .min(2, ({ min }) =>
                formatMessage(
                    {
                        id: 'schema.errors.min',
                        defaultMessage: '{field} must be at least {min} characters long',
                        description:
                            'Error message displayed when user enters a value that is shorter than the required minimum length. Used in forms',
                    },
                    { field: formatMessage({ id: 'common.lastName', defaultMessage: 'Last name' }), min }
                )
            )
            .max(50, ({ max }) =>
                formatMessage(
                    {
                        id: 'schema.errors.max',
                        defaultMessage: '{field} cannot exceed {max} characters',
                        description:
                            'Error message displayed when user enters a value that exceeds the maximum allowed length. Used in forms',
                    },
                    { field: formatMessage({ id: 'common.lastName', defaultMessage: 'Last name' }), max }
                )
            ),
        email: Yup.string()
            .required(
                formatMessage(
                    {
                        id: 'schema.errors.required',
                        defaultMessage: '{field} is required',
                        description: 'Error message displayed when user leaves a mandatory field empty. Used in forms',
                    },
                    { field: formatMessage({ id: 'common.email', defaultMessage: 'Email' }) }
                )
            )
            .matches(
                EMAIL_REGEX,
                formatMessage(
                    {
                        id: 'schema.errors.invalid',
                        defaultMessage: '{field} is invalid',
                        description:
                            'Error message displayed when user enters a value that does not match the required format. Used in forms',
                    },
                    { field: formatMessage({ id: 'common.email', defaultMessage: 'Email' }) }
                )
            ),
        company_name: Yup.string().when('requested_role', {
            is: (role: string) => role && role !== 'end_user',
            then: (schema) =>
                schema.required(
                    formatMessage(
                        {
                            id: 'schema.errors.required',
                            defaultMessage: '{field} is required',
                            description:
                                'Error message displayed when user leaves a mandatory field empty. Used in forms',
                        },
                        { field: formatMessage({ id: 'common.company', defaultMessage: 'Company name' }) }
                    )
                ),
            otherwise: (schema) => schema.optional(),
        }),
        country: Yup.string().required(
            formatMessage(
                {
                    id: 'schema.errors.required',
                    defaultMessage: '{field} is required',
                    description: 'Error message displayed when user leaves a mandatory field empty. Used in forms',
                },
                { field: formatMessage({ id: 'common.country', defaultMessage: 'Country' }) }
            )
        ),
        language: Yup.string().optional(),
        requested_role: Yup.string().optional(),
        recaptcha_token: Yup.string().required(
            formatMessage(
                {
                    id: 'schema.errors.required',
                    defaultMessage: '{field} is required',
                    description: 'Error message displayed when user leaves a mandatory field empty. Used in forms',
                },
                { field: 'reCAPTCHA verification' }
            )
        ),
    });
